import { PageTitle } from "@client/components/round/PageTitle";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import ThemeSelector from "@client/components/utils/ThemeSelector";
import userIsAdmin from "@client/utils/userIsAdmin";
import { type Track, trackNames } from "@lib/types";
import { sleep } from "@lib/utils/sleep";
import { useContext, useState } from "react";
import { DispatchContext, StateContext } from "./Round";

const SettingsComponent = withErrorBoundary(() => {
  const context = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const { mode, setMode, actingAsUserId, round, userProfiles } = context;
  const isAdmin = userIsAdmin(actingAsUserId, context);
  const [isLoading, setIsLoading] = useState<"user" | "track" | null>(null);
  const realTrack = round.members[actingAsUserId].track;

  function onUserChange(event: React.ChangeEvent<HTMLSelectElement>) {
    void viewAsUser(event.target.value as string);
  }

  async function viewAsUser(userId: string | null): Promise<void> {
    if (isLoading != null) return;
    setIsLoading("user");
    await sleep(0); // Give up the thread to let the UI update
    await setMode({
      ...mode,
      viewAsTrackMember: null,
      viewAsUserId: userId,
    });
    setIsLoading(null);
  }

  const member = round.members[actingAsUserId];
  if (!member) {
    throw new Error(`Member not found for userId: ${actingAsUserId}`);
  }
  const {
    feedbackVisibleByDefault,
    evaluableFeedback,
    evaluableNotes,
    evaluablePermissions,
  } = member;

  function onFeedbackVisibleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    void dispatch({
      type: "ROUND_MEMBER_PROPERTY_CHANGE",
      memberId: actingAsUserId,
      feedbackVisibleByDefault: !checked,
    });
  }

  function onTrackChange(event: React.ChangeEvent<HTMLSelectElement>) {
    void viewAsTrackMember(event.target.value as Track);
  }

  async function viewAsTrackMember(track: Track | null) {
    if (isLoading != null) return;
    setIsLoading("track");
    await sleep(0); // Give up the thread to let the UI update
    await setMode({
      ...mode,
      viewAsTrackMember: track,
      viewAsUserId: null,
    });
    setIsLoading(null);
  }

  const possiblyAffectedProposals = new Set([
    ...Object.keys(evaluableNotes),
    ...Object.keys(evaluableFeedback),
  ]);
  const proposalsWhichWouldChange = Array.from(
    possiblyAffectedProposals,
  ).filter((key) => evaluablePermissions[key] ?? "default" === "default");

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="p-4 pb-2">
        <PageTitle title="Settings" />
      </div>
      <div className="flex-1 overflow-y-auto p-4 pt-0">
        {isAdmin && realTrack === "NO_TRACK" && (
          <>
            <div className="flex h-10 p-2 pl-0 items-center">
              <label htmlFor="user-select" className="pr-2">
                Impersonate:
              </label>
              <select
                id="user-select"
                value={mode.viewAsUserId ?? ""}
                onChange={onUserChange}
                className="dark:bg-stone-800 border h-6 border-gray-700 mr-2 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading != null}
              >
                <option value="" disabled>
                  Select a user...
                </option>
                {Object.entries(userProfiles).map(([key, userProfile]) => (
                  <option
                    key={key}
                    value={key}
                    disabled={key === actingAsUserId}
                  >
                    {userProfile.name} ({userProfile.track}, {userProfile.email}
                    )
                  </option>
                ))}
              </select>
              {isLoading === "user" && <span>Loading...</span>}
              {mode.viewAsUserId != null && (
                <button onClick={() => void viewAsUser(null)}>Clear</button>
              )}
            </div>
            <div className="flex h-10 p-2 pl-0 items-center">
              <label htmlFor="track-select" className="pr-2">
                View as track member:
              </label>
              <select
                id="track-select"
                value={mode.viewAsTrackMember ?? ""}
                onChange={onTrackChange}
                className="dark:bg-stone-800 border h-6 border-gray-700 mr-2 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading != null}
              >
                <option value="" disabled>
                  Select a track...
                </option>
                {Object.entries(trackNames)
                  .filter(([key]) => key !== member.track)
                  .map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
              </select>
              {isLoading === "track" && <span>Loading...</span>}
              {mode.viewAsTrackMember != null && (
                <button onClick={() => viewAsTrackMember(null)}>Clear</button>
              )}
            </div>
          </>
        )}
        <div className="flex h-10 py-2 items-center gap-2">
          <div className="pr-2">Theme:</div>
          <ThemeSelector />
        </div>
        <div className="flex flex-col h-10 py-2">
          <div className="flex">
            <label className="pr-2" htmlFor="feedback-visible-checkbox">
              Hide notes and feedback from other tracks by default:
            </label>
            <input
              id="feedback-visible-checkbox"
              type="checkbox"
              checked={!feedbackVisibleByDefault}
              onChange={onFeedbackVisibleChange}
            />
          </div>
          {proposalsWhichWouldChange.length > 0 && (
            <div className="text-red-500">
              Warning: Notes and feedback on {proposalsWhichWouldChange.length}{" "}
              {proposalsWhichWouldChange.length === 1
                ? "proposal"
                : "proposals"}{" "}
              would become{" "}
              {feedbackVisibleByDefault ? "hidden from" : "visible to"} other
              tracks.
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Export both as named export and default export
export const Settings = SettingsComponent;
export default Settings;
